import React, { useMemo, useState } from 'react';
import { Layout, Menu, Avatar, Dropdown, Button, Space, theme } from 'antd';
import { 
    UserOutlined,
    MenuFoldOutlined,
    MenuUnfoldOutlined,
    LogoutOutlined,
    ShoppingOutlined
} from '@ant-design/icons';
import { CustomerServiceOutlined, QuestionCircleOutlined, TeamOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { setUserData, userLogout } from '../../redux/actions/DataActions';
import { successMsg } from '../../utils/ShowMessage';
import { routes } from '../../utils/routes.config';
import { ApiServices } from '../../services/apiServices';
import { ApiConfig } from '../../services/ApiConfigs';
import { FaTachometerAlt, FaWarehouse, FaCog, FaCar, FaCode, FaMapMarkerAlt } from "react-icons/fa";
import { BellOutlined } from "@ant-design/icons";
import { HiShoppingCart } from 'react-icons/hi';

const { Header, Sider, Content, Footer } = Layout;

const MainLayout = ({ children }) => {
    const [collapsed, setCollapsed] = useState(false);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const userDetail = useSelector((state) => state.UserData.userData);
    const {
        token: { colorBgContainer, borderRadiusLG },
    } = theme.useToken();
    const isSuperAdmin = userDetail?.role === "superAdmin";
    const userDbRole = userDetail?.user?.role; // super_admin | store_owner | staff
    const isStaff = userDbRole === 'staff';
    const perms = userDetail?.permissions || {};

    const can = (permKey) => {
        if (!isStaff) return true;
        return !!perms?.[permKey];
    };

    const handleLogout = async () => {
        try {
            // Call logout API
            await ApiServices('post', ApiConfig.LOGOUT, {});
            
            // Clear user data from Redux store
            dispatch(userLogout());
            dispatch(setUserData({}));
            
            // Navigate to login page
            navigate(routes.LOGIN);
            successMsg("Logged out successfully!");
        } catch (error) {
            console.error('Logout error:', error);
            // Even if API call fails, clear local state and logout
            dispatch(userLogout());
            dispatch(setUserData({}));
            navigate(routes.LOGIN);
            successMsg("Logged out successfully!");
        }
    };

    const menuItemsRaw = isSuperAdmin ? [
        {
            key: routes.SUPER_DASHBOARD,
            icon: <FaTachometerAlt size={15} />,
            label: 'Dashboard',
        },
        {
            key: routes.PLANS,
            icon: <FaCode size={15} />,
            label: 'Plans',
        },
        {
            key: routes.SUPPORT,
            icon: <CustomerServiceOutlined />,
            label: 'Contact & Support',
        },
        {
            key: routes.SETTINGS,
            icon: <FaCog size={15} />,
            label: 'Setting',
        },
        {
            key: routes.CUSTOM_NOTIFICATIONS,
            icon: <BellOutlined />,
            label: 'Notifications',
        },
        {
            key: routes.API_DOCUMENTATION,
            icon: <FaCode size={15} />,
            label: 'API Docs',
        },
        {
            key: routes.USER_GUIDE,
            icon: <QuestionCircleOutlined />,
            label: 'User Guide',
        },
    ] : [
        {
            key: routes.DASHBOARD,
            icon: <FaTachometerAlt size={15} />,
            label: 'Dashboard',
        },
        {
            key: routes.Orders,
            icon: <HiShoppingCart size={15} />,
            label: 'Orders',
        },
        {
            key: routes.Drivers,
            icon: <FaCar size={15} />,
            label: 'Drivers',
        },
        {
            key: routes.LIVE_TRACKING,
            icon: <FaMapMarkerAlt size={15} />,
            label: 'Live Tracking',
        },
        {
            key: routes.Warehouse,
            icon: <FaWarehouse size={15} />,
            label: 'Warehouse',
        },
        // Staff Management (store owner only; super admin uses different area)
        ...(isStaff ? [] : [{
            key: routes.STAFF,
            icon: <TeamOutlined />,
            label: 'Staff',
        }]),
        {
            key: routes.SELECT_PLAN,
            icon: <ShoppingOutlined size={15} />,
            label: 'Plans',
        },
        {
            key: routes.SUPPORT,
            icon: <CustomerServiceOutlined />,
            label: 'Contact & Support',
        },
        {
            key: routes.SETTINGS,
            icon: <FaCog size={15} />,
            label: 'Setting',
        },
        {
            key: routes.CUSTOM_NOTIFICATIONS,
            icon: <BellOutlined />,
            label: 'Notifications',
        },
        {
            key: routes.API_DOCUMENTATION,
            icon: <FaCode size={15} />,
            label: 'API Docs',
        },
        {
            key: routes.USER_GUIDE,
            icon: <QuestionCircleOutlined />,
            label: 'User Guide',
        },
    ];

    // Permission-based filtering for staff
    const menuItems = useMemo(() => {
        if (!isStaff) return menuItemsRaw;
        return menuItemsRaw.filter((it) => {
            if (it.key === routes.DASHBOARD) return can('dashboard.read');
            if (it.key === routes.Orders) return can('orders.read');
            if (it.key === routes.Drivers) return can('drivers.read');
            if (it.key === routes.Warehouse) return can('warehouse.read');
            if (it.key === routes.LIVE_TRACKING) return can('live_tracking.read');
            if (it.key === routes.CUSTOM_NOTIFICATIONS) return can('notifications.read');
            if (it.key === routes.SETTINGS) return can('settings.read');
            if (it.key === routes.API_DOCUMENTATION) return can('api_docs.read');
            if (it.key === routes.USER_GUIDE) return true;
            return true;
        });
    }, [isStaff, menuItemsRaw, perms]);

    const userMenuItems = [
        {
            key: 'logout',
            icon: <LogoutOutlined />,
            label: 'Logout',
            onClick: handleLogout,
        },
    ];

    return (
        <Layout style={{ minHeight: '100vh' }}>
            <Sider 
                trigger={null} 
                collapsible 
                collapsed={collapsed}
                style={{
                    overflow: 'auto',
                    height: '100vh',
                    position: 'fixed',
                    left: 0,
                    top: 0,
                    bottom: 0,
                }}
                theme="dark"
            >
                <div style={{ 
                    height: 64, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    padding: '16px',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                }}>
                    {collapsed ? (
                        <FaCode style={{ color: 'white', fontSize: 24 }} />
                    ) : (
                        <Space>
                            <img 
                                src='/image/logo.png' 
                                alt="Logo" 
                                style={{ height: 30, width: 50, objectFit: 'contain' }} 
                            />
                            <span style={{ color: 'white', fontWeight: 'bold' }}>Magecomp</span>
                        </Space>
                    )}
                </div>
                <Menu
                    theme="dark"
                    mode="inline"
                    selectedKeys={[location.pathname]}
                    items={menuItems}
                    onClick={({ key }) => navigate(key)}
                    style={{ borderRight: 0 }}
                />
            </Sider>
            <Layout
                style={{
                    marginLeft: collapsed ? 80 : 200,
                    transition: 'all 0.2s',
                    minHeight: '100vh',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <Header
                    style={{
                        padding: '0 24px',
                        background: colorBgContainer,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        lineHeight: 'normal',
                        height: 64,
                    }}
                >
                    <Button
                        type="text"
                        icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                        onClick={() => setCollapsed(!collapsed)}
                        style={{
                            fontSize: '16px',
                            width: 64,
                            height: 64,
                        }}
                    />
                    <Space size="middle">
                        <Space direction="vertical" size={0} style={{ textAlign: 'right' }}>
                            <span style={{ fontWeight: 'bold', fontSize: 14 }}>
                                {(() => {
                                    const user = userDetail?.user || userDetail;
                                    if (user?.firstname && user?.lastname) {
                                        return `${user.firstname} ${user.lastname}`;
                                    }
                                    if (user?.firstname) {
                                        return user.firstname;
                                    }
                                    return userDetail?.role === "user" ? "Admin" : "Super Admin";
                                })()}
                            </span>
                            <span style={{ fontSize: 12, color: '#999' }}>
                                {userDetail?.role === "user" ? "Admin" : "Super Admin"}
                            </span>
                        </Space>
                        <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
                            <Avatar 
                                size="large" 
                                icon={<UserOutlined />} 
                                style={{ cursor: 'pointer' }}
                            />
                        </Dropdown>
                    </Space>
                </Header>
                <Content
                    style={{
                        margin: 8,
                        padding: 8,
                        minHeight: 280,
                        background: colorBgContainer,
                        borderRadius: borderRadiusLG,
                        flex: 1,
                    }}
                >
                    {children}
                </Content>
                <Footer
                    style={{
                        textAlign: 'center',
                        background: '#fff',
                        borderTop: '1px solid #f0f0f0',
                        padding: '12px 16px',
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 700, color: '#1e293b' }}>Bharat Desai</span>
                        <span style={{ color: '#d9d9d9' }}>|</span>
                        <span style={{ color: '#64748b' }}>
                            Contact:{' '}
                            <a
                                href="tel:+917984802905"
                                style={{ color: '#1677ff', fontWeight: 600, textDecoration: 'none' }}
                            >
                                +91 79848 02905
                            </a>
                        </span>
                        <span style={{ color: '#d9d9d9' }}>|</span>
                        <span style={{ color: '#94a3b8', fontSize: 12 }}>
                            © {new Date().getFullYear()}
                        </span>
                    </div>
                </Footer>
            </Layout>
        </Layout>
    );
};

export default MainLayout;

